<template>
  <v-form
    ref="formRef"
    v-model="valid"
    class="company-profile-form"
    @submit.prevent="onSubmit"
  >
    <div
      :class="[
        'company-profile-form__fields',
        renderSaveButton && 'company-profile-form__fields--with-sticky-save',
      ]"
    >
    <h3 class="text-h6 mb-4">{{ t(strings.contactDetailsSectionTitle) }}</h3>
    <v-text-field
      v-model="name"
      :label="t(strings.nameLabel)"
      :placeholder="t(strings.namePlaceholder)"
      v-bind="commonInputProps"
      :rules="[requiredRule]"
    />
    <v-text-field
      v-model="dba"
      :label="t(strings.dbaLabel)"
      :placeholder="t(strings.dbaPlaceholder)"
      v-bind="commonInputProps"
      :rules="[dbaRule]"
    />
    <UsPhoneNumberInput
      v-model="phone"
      v-bind="commonInputProps"
      :label="t(strings.phoneLabel)"
      :placeholder="t(strings.phonePlaceholder)"
      :rules="[phoneOptionalRule]"
    />
    <v-text-field
      v-model="email"
      :label="t(strings.emailLabel)"
      :placeholder="t(strings.emailPlaceholder)"
      v-bind="commonInputProps"
      :rules="[emailRule]"
      type="email"
    />

    <v-divider class="my-6" />

    <h3 class="text-h6 mb-4">{{ t(strings.companyInfoSectionTitle) }}</h3>

    <!-- Logo Section -->
    <h4 class="text-subtitle-1 font-weight-medium mb-4">
      {{ t(strings.logoSectionTitle) }}
    </h4>
    <v-container class="mb-4">
      <v-row>
        <v-col cols="3">
          <div class="d-flex align-center">
            <!-- Logo Image -->
            <v-img
              v-if="logoUrl"
              :src="logoUrl"
              :alt="t(strings.logoAlt)"
              class="rounded"
              width="100%"
              @error="() => {}"
            >
              <template v-slot:error>
                <v-icon color="grey-lighten-1" size="large"
                  >mdi-image-off</v-icon
                >
              </template>
            </v-img>
            <div
              v-else
              class="mr-4 d-flex align-center justify-center"
              style="
                width: 100%;
                height: 100px;
                border: 2px dashed #ccc;
                border-radius: 4px;
              "
            >
              <v-icon color="grey-lighten-1" size="large">mdi-image</v-icon>
            </div>
          </div>
        </v-col>
        <v-col cols="9">
          <!-- Logo Upload -->
          <div>
            <v-file-input
              v-model="selectedLogoFile"
              :accept="acceptedImageTypes"
              :disabled="uploadingLogo"
              :prepend-icon="undefined"
              :append-icon="undefined"
              @change="onLogoSelect"
              :placeholder="t(strings.selectLogoFile)"
              persistent-hint
              variant="outlined"
              density="comfortable"
              class="mb-2"
            >
              <template v-slot:selection="{ fileNames }">
                <template
                  v-for="(fileName, index) in fileNames"
                  :key="fileName"
                >
                  <v-chip
                    v-if="index < 1"
                    :key="fileName"
                    size="small"
                    class="me-2"
                    color="primary"
                    variant="tonal"
                  >
                    {{ fileName }}
                  </v-chip>
                </template>
              </template>
            </v-file-input>

            <!-- Upload Progress -->
            <v-progress-linear
              v-if="uploadingLogo"
              :model-value="logoUploadProgress"
              color="primary"
              class="mb-2"
            ></v-progress-linear>

            <!-- Upload Error -->
            <v-alert
              v-if="logoUploadError"
              type="error"
              variant="tonal"
              class="mb-2"
              closable
              @click:close="logoUploadError = null"
            >
              {{ logoUploadError }}
            </v-alert>
          </div>

          <!-- Remove Logo Button -->
          <v-btn
            v-if="logoUrl"
            class="float-end"
            color="error"
            variant="flat"
            @click="logoUrl = undefined"
          >
            Remove Logo
          </v-btn>
        </v-col>
      </v-row>
    </v-container>

    <v-text-field
      v-model.number="yearFounded"
      :label="t(strings.yearFoundedLabel)"
      :placeholder="t(strings.yearFoundedPlaceholder)"
      v-bind="commonInputProps"
      :rules="[yearFoundedRule]"
      type="number"
      min="1800"
      max="2100"
    />

    <h4 class="text-subtitle-1 font-weight-medium mb-4">
      {{ t(strings.addressSectionTitle) }}
    </h4>
    <AddressForm v-model="address" />

    <v-text-field
      v-model="website"
      :label="t(strings.websiteLabel)"
      :placeholder="t(strings.websitePlaceholder)"
      v-bind="commonInputProps"
      :rules="[websiteRule]"
      type="url"
    />

    <h4 class="text-subtitle-1 font-weight-medium mb-4 mt-2">
      {{ t(strings.socialUrlsSectionTitle) }}
    </h4>
    <SocialUrlsList
      :model-value="socialUrls"
      @update:model-value="onSocialUrlsUpdate"
    />
    </div>

    <div
      v-if="renderSaveButton"
      class="company-profile-form__save-bar bg-surface d-flex flex-column"
    >
      <v-btn
        type="submit"
        :loading="submitting"
        :disabled="!valid"
        block
        variant="flat"
        color="primary"
        size="large"
        rounded="lg"
        class="company-profile-form__save-btn text-none font-weight-semibold"
      >
        {{ t(strings.save) }}
      </v-btn>
    </div>
  </v-form>
</template>

<script setup lang="ts">
import { company_profile_form_strings as strings } from "./CompanyProfileForm.strings.ts";
import { useReverseT } from "../../i18n.ts";
import { useCreateOrganization } from "../../requests/organizations/create.ts";
import { useUpdateOrganization } from "../../requests/organizations/update.ts";
import { useUploadOrganizationLogo } from "../../requests/organizations/upload-logo.ts";
import type {
  Organization,
  OrganizationRequest,
} from "@vendata/power-up-spec";
import { ref, computed } from "vue";
// import CompanyAddressForm from "../../components/CompanyAddressForm.vue";
import { AddressForm } from "@saflib/sdk/components";
import SocialUrlsList from "../../components/SocialUrlsList.vue";
import type { Address } from "@saflib/openapi";
import { UsPhoneNumberInput } from "@saflib/vue/components";

const requiredRule = (v: any) => {
  if (v) return true;
  return "Required";
};

const emailRule = (v: any) => {
  if (!v) return true;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(v) || "Please enter a valid email address";
};

const phoneOptionalRule = (v: any) => {
  if (!v) return true;
  return true;
};

const websiteRule = (v: any) => {
  if (!v) return true; // Let required rule handle empty values
  const urlRegex = /^https?:\/\/.+/;
  return (
    urlRegex.test(v) ||
    "Please enter a valid website URL (starting with http:// or https://)"
  );
};

const yearFoundedRule = (v: any) => {
  if (!v) return true; // Optional field
  if (typeof v !== "number") return "Year founded must be a number";
  if (v < 1800 || v > 2100) return "Year founded must be between 1800 and 2100";
  if (!Number.isInteger(v)) return "Year founded must be a whole number";
  return true;
};

const dbaRule = (v: any) => {
  if (!v) return true; // Optional field
  if (typeof v !== "string") return "DBA must be text";
  if (v.length > 255) return "DBA must be less than 255 characters";
  return true;
};

const { t } = useReverseT();
const commonInputProps = {
  variant: "outlined" as const,
  density: "comfortable" as const,
  class: "mb-4",
};

// Props
const props = withDefaults(
  defineProps<{
    organization?: Organization | OrganizationRequest;
    renderSaveButton?: boolean;
  }>(),
  {
    renderSaveButton: true,
  },
);

// Form state
const formRef = ref();
const valid = ref(false);

// Form data refs
const name = ref<string>(props.organization?.name || "");
const dba = ref<string | undefined>(props.organization?.dba || undefined);
const email = ref<string>(props.organization?.email ?? "");
const phone = ref<string>(props.organization?.phone ?? "");
const website = ref<string | undefined>(
  props.organization?.website || undefined,
);
const yearFounded = ref<number | undefined>(
  props.organization?.year_founded || undefined,
);
const address = ref<Address | null>(props.organization?.address ?? null);
const socialUrls = ref<Array<{ url: string }>>(
  props.organization?.social_urls || [],
);
const logoUrl = ref<string | undefined>(
  props.organization?.logo_url || undefined,
);

/** Preserved on save — intents are no longer edited in this form. */
const persistedIntentFlags = computed(() => {
  const o = props.organization;
  if (!o || !("intends_to_sell_parts" in o)) {
    return { sell: false, buy: false };
  }
  return {
    sell: !!o.intends_to_sell_parts,
    buy: !!o.intends_to_buy_parts,
  };
});

// Logo upload state
const selectedLogoFile = ref<File | null>(null);
const uploadingLogo = ref(false);
const logoUploadProgress = ref(0);
const logoUploadError = ref<string | null>(null);

// Logo upload configuration
const acceptedImageTypes =
  "image/jpeg,image/jpg,image/png,image/gif,image/webp";

// Computed properties
const isUpdate = computed(
  () =>
    props.organization &&
    "id" in props.organization &&
    !!props.organization.id,
);

// Mutations
const createMutation = useCreateOrganization();
const updateMutation = useUpdateOrganization();
const uploadLogoMutation = useUploadOrganizationLogo();
const submitting = computed(
  () => createMutation.isPending.value || updateMutation.isPending.value,
);

// Logo upload handler
const onLogoSelect = async () => {
  const file = selectedLogoFile.value;
  if (!file) return;
  uploadingLogo.value = true;
  logoUploadProgress.value = 0;
  logoUploadError.value = null;

  try {
    // Upload logo
    const result = await uploadLogoMutation.mutateAsync({
      file: file,
    });

    // Update logo URL
    logoUrl.value = result.logo_url || undefined;

    // Reset form
    selectedLogoFile.value = null;
    logoUploadProgress.value = 100;
  } catch (error) {
    console.error("Logo upload failed:", error);
    logoUploadError.value =
      error instanceof Error ? error.message : "Logo upload failed";
  } finally {
    uploadingLogo.value = false;
    logoUploadProgress.value = 0;
  }
};

// Emits
const emit = defineEmits<{
  success: [organization: Organization];
  cancel: [];
}>();

// Form submission
const onSubmit = async () => {
  const validation = await formRef.value?.validate();
  if (validation && !validation.valid) return;
  if (!valid.value) return;

  try {
    const data: OrganizationRequest = {
      name: name.value,
      dba: dba.value || undefined,
      email: email.value.trim() || null,
      phone: phone.value.trim() || null,
      website: website.value || undefined,
      year_founded: yearFounded.value || undefined,
      address: address.value || undefined,
      social_urls: socialUrls.value.length > 0 ? socialUrls.value : undefined,
      logo_url: logoUrl.value || undefined,
      intends_to_sell_parts: persistedIntentFlags.value.sell,
      intends_to_buy_parts: persistedIntentFlags.value.buy,
    };

    let result;
    if (isUpdate.value) {
      result = await updateMutation.mutateAsync(data);
    } else {
      result = await createMutation.mutateAsync(data);
    }

    emit("success", result);
  } catch (error) {
    console.error(
      `Failed to ${isUpdate.value ? "update" : "create"} company profile:`,
      error,
    );
    throw error; // Re-throw so parent can handle
  }
};

// Expose save function for external use
const save = async () => {
  return await onSubmit();
};

// Expose the save function
defineExpose({
  save,
});

// Social URLs handler
const onSocialUrlsUpdate = (value: Array<{ url: string }> | null) => {
  socialUrls.value = value || [];
};
</script>

<style scoped>
.company-profile-form__fields--with-sticky-save {
  /* Space for the fixed save bar so fields aren’t covered */
  padding-bottom: 7rem;
}

/*
 * Fixed to the viewport so the bar sits at the bottom of the screen (not just above v-footer).
 * Sticky bottom was anchoring to v-main, which ends above the marketing footer.
 */
.company-profile-form__save-bar {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1006;
  box-sizing: border-box;
  width: 100%;
  max-width: 100%;
  margin-top: 0;
  padding: 0.75rem 1rem;
  padding-bottom: calc(0.75rem + env(safe-area-inset-bottom, 0px));
  border-top: thin solid rgba(0, 0, 0, 0.08);
  box-shadow: 0 -4px 16px rgba(0, 0, 0, 0.08);
}

.company-profile-form__save-btn {
  width: 100%;
  min-width: 0;
  align-self: stretch;
}
</style>
